import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, Gift, Sparkles, Minus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, login } from "../context/AuthContext";
import { SiteLayout } from "../components/site/SiteLayout";
import { fmtDate, startCheckout, startTrial, openPortal, errMsg } from "../lib/billing";

const usd = (n) => `$${Number(n).toFixed(2)}`;

const Feature = ({ children, on = true }) => (
  <li className={`flex gap-2 items-start ${on ? "" : "text-stone-400"}`}>
    {on ? <Check size={18} className="mt-1 shrink-0 text-[var(--jade)]" /> : <Minus size={18} className="mt-1 shrink-0" />}<span>{children}</span>
  </li>
);

const PlanBox = ({ testid, icon, name, price, unit, desc, features, cta, highlight, delay }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} data-testid={testid}
    className={`relative rounded-[2rem] border-4 p-7 flex flex-col bg-white ${highlight ? "border-[var(--vermilion)] shadow-2xl lg:-translate-y-3" : "border-[var(--line)] shadow-md"}`}>
    {highlight && <span className="absolute -top-4 left-7 px-4 py-1 rounded-full bg-[var(--vermilion)] text-white text-sm font-bold">最受家長歡迎</span>}
    <div className="flex items-center gap-3">{icon}<h2 className="text-2xl font-black text-[var(--tea)]">{name}</h2></div>
    <div className="mt-5 flex items-end gap-1"><span className="text-5xl font-black text-[var(--ink)]" data-testid={`${testid}-price`}>{price}</span><span className="text-stone-500 mb-1">{unit}</span></div>
    <p className="text-stone-600 mt-2 min-h-[48px]">{desc}</p>
    <ul className="mt-5 space-y-2 text-[15px] flex-1">{features}</ul>
    <div className="mt-7">{cta}</div>
  </motion.div>
);

const Btn = ({ testid, onClick, disabled, children, tone = "red" }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`kid-btn w-full text-lg disabled:opacity-60 ${tone === "red" ? "bg-[var(--vermilion)] text-white" : tone === "jade" ? "bg-[var(--jade)] text-white" : "bg-white border-2 border-[var(--line)] text-[var(--tea)]"}`}>{children}</button>
);

const FAQ = [
  ["試用會自動扣款嗎？", "不會。7 天試用不需要信用卡，到期自動回到免費方案，學習紀錄都會保留。"],
  ["可以隨時取消訂閱嗎？", "可以。在家長報告頁按「管理訂閱」即可取消，已付費的期間結束前都能繼續使用。"],
  ["一個帳號可以給幾個孩子用？", "免費方案可建立 1 個孩子檔案，Premium 最多 4 個，每個孩子的進度分開記錄。"],
  ["付款安全嗎？", "付款由 Stripe 處理，我們不會看到或儲存您的信用卡資料。"],
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [interval, setInterval] = useState("year");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/billing/plans").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <SiteLayout><div className="min-h-[60vh]" /></SiteLayout>;
  const a = user?.access;
  const save = Math.round((1 - cfg.yearly_price / (cfg.monthly_price * 12)) * 100);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); setBusy(false); } };
  const trial = () => run(async () => { await startTrial(); await refresh(); setBusy(false); toast.success("已開始 Premium 試用！"); nav("/profiles"); });
  const paid = a && ["stripe", "admin", "grant"].includes(a.source);

  const starterCta = !user ? <Btn testid="plan-starter-btn" tone="white" onClick={login}>免費開始</Btn>
    : <Btn testid="plan-starter-btn" tone="white" disabled={a?.plan === "starter"} onClick={() => nav("/profiles")}>{a?.plan === "starter" ? "目前方案" : "進入故事館"}</Btn>;
  const trialCta = !user ? <Btn testid="plan-trial-btn" tone="jade" onClick={login}>登入後開始試用</Btn>
    : a?.plan === "trial" ? <Btn testid="plan-trial-btn" tone="jade" disabled>試用中・到 {fmtDate(a.trial_ends_at)}</Btn>
    : <Btn testid="plan-trial-btn" tone="jade" disabled={busy || a?.trial_used || paid} onClick={trial}>{paid ? "已是 Premium" : a?.trial_used ? "已使用過試用" : `開始 ${cfg.trial_days} 天免費試用`}</Btn>;
  const premiumCta = !user ? <Btn testid="plan-premium-btn" onClick={login}>登入後訂閱</Btn>
    : a?.source === "stripe" ? <Btn testid="plan-premium-btn" tone="white" disabled={busy} onClick={() => run(openPortal)}>管理訂閱</Btn>
    : paid ? <Btn testid="plan-premium-btn" disabled>已享有完整 Premium</Btn>
    : <Btn testid="plan-premium-btn" disabled={busy} onClick={() => run(() => startCheckout(interval))}>{busy ? "前往付款中…" : `訂閱${interval === "year" ? "年繳" : "月繳"}方案`}</Btn>;

  const common = [`經典故事館前 ${cfg.free_classic} 個故事`, `生活中文館第 ${cfg.free_life_themes} 個主題`, `我會說中文館前 ${cfg.free_speak} 課`];
  return (
    <SiteLayout>
      <div className="font-serif-tc" data-testid="pricing-page">
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-14 pb-6">
          <div className="text-[var(--vermilion)] font-bold tracking-[0.3em] text-sm">方案與價格</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[var(--ink)] mt-3 leading-tight">先免費試，<br className="sm:hidden" />喜歡再升級</h1>
          <p className="text-base md:text-lg text-stone-600 mt-4 max-w-2xl">注音練習館永遠免費。想讓孩子走完所有故事館的課程，就選 Premium。</p>
          <div className="mt-8 inline-flex p-1.5 rounded-full bg-white border-2 border-[var(--line)]" data-testid="pricing-interval-toggle">
            {[["month", "月繳"], ["year", "年繳"]].map(([k, l]) => (
              <button key={k} data-testid={`pricing-interval-${k}`} onClick={() => setInterval(k)}
                className={`h-11 px-6 rounded-full font-bold transition-colors ${interval === k ? "bg-[var(--ink)] text-white" : "text-[var(--tea)]"}`}>
                {l}{k === "year" && save > 0 && <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${interval === k ? "bg-amber-300 text-[var(--ink)]" : "bg-amber-100"}`}>省 {save}%</span>}
              </button>
            ))}
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid lg:grid-cols-3 gap-6 items-stretch">
          <PlanBox testid="plan-starter" delay={0} icon={<Sparkles className="text-stone-500" />} name="免費 Starter" price="$0" unit="/ 永久" desc="先帶孩子認識每一座學習館，看看喜不喜歡。" cta={starterCta}
            features={<>{common.map((f) => <Feature key={f}>{f}</Feature>)}<Feature>注音練習館 37 個注音全部開放</Feature><Feature>小書童畫坊：自由畫板、各 3 個塗色和跟著畫、文化美術和故事角色各 1 個，保存 3 幅作品</Feature><Feature>{cfg.free_children} 個孩子檔案</Feature><Feature>總覽學習報告</Feature><Feature on={false}>分館詳細報告</Feature></>} />
          <PlanBox testid="plan-trial" delay={0.08} icon={<Gift className="text-[var(--jade)]" />} name="Premium 試用" price="$0" unit={`/ ${cfg.trial_days} 天`} desc="不用信用卡，到期自動回到免費方案，不會扣款。" cta={trialCta}
            features={<><Feature>{cfg.trial_days} 天內使用 Premium 全部內容</Feature><Feature>不需要信用卡</Feature><Feature>到期不會自動扣款</Feature><Feature>學習紀錄全部保留</Feature><Feature>每個帳號可試用一次</Feature></>} />
          <PlanBox testid="plan-premium" delay={0.16} highlight icon={<Crown className="text-amber-500" />} name="Premium"
            price={interval === "year" ? usd(cfg.yearly_price) : usd(cfg.monthly_price)} unit={interval === "year" ? "/ 年" : "/ 月"}
            desc={interval === "year" ? `平均每月 ${usd(cfg.yearly_price / 12)}，自動續訂，可隨時取消。` : "每月自動續訂，可隨時取消。"} cta={premiumCta}
            features={<><Feature>經典、生活、我會說三館所有故事與課程</Feature><Feature>注音練習館全部功能</Feature><Feature>小書童畫坊全部 31 個美術活動、所有畫框和畫廊背景</Feature><Feature>完整作品集、虛擬小畫廊、家庭與社群分享</Feature><Feature>最多 {cfg.premium_children} 個孩子檔案</Feature><Feature>分館詳細學習報告</Feature><Feature>家長錄音室全部內容</Feature><Feature>「只聽故事」全部故事</Feature></>} />
        </section>
        <section className="max-w-4xl mx-auto px-6 sm:px-10 pb-20" data-testid="pricing-faq">
          <h2 className="text-lg font-black text-[var(--tea)] mb-5">常見問題</h2>
          <div className="grid gap-3">
            {FAQ.map(([q, ans]) => (
              <div key={q} className="scroll-card !rounded-2xl p-5"><div className="font-bold text-[var(--ink)]">{q}</div><p className="text-stone-600 mt-1">{ans}</p></div>
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
